<template>
  <div
    class="cm-stat-table-container"
    :class="{ 'cm-stat-table-container--long': isLongTable }"
    :style="{ width: `${width}px` }"
  >
    <h2
      v-if="title"
      class="cm-stat-table__title"
    >{{ title }}</h2>

    <Input
      v-if="filterable"
      :value.sync="filterInput"
      :placeholder="placeholder"
      class="cm-stat-table__search-input"
    />

    <table class="cm-stat-table">
      <thead>
        <tr>
          <td
            v-for="(col, index) in cols"
            :key="index">
            {{ col.label }}
          </td>
        </tr>
      </thead>

      <tbody
        v-if="isLoading"
        class="cm-stat-table__body--loading"
      >
        <tr>
          <td :colspan="keys.length">
            <spinner-dots :size="width / 5" />
          </td>
        </tr>
      </tbody>

      <tbody
        class="cm-stat-table__body--error"
        v-else-if="isError"
      >
        <tr>
          <td :colspan="keys.length">
            Ошибка при получении данных
          </td>
        </tr>
      </tbody>

      <tbody
        class="cm-stat-table__body--empty"
        v-else-if="!tableData.length"
      >
        <tr>
          <td :colspan="keys.length">
            Нет данных
          </td>
        </tr>
      </tbody>

      <tbody
        class="cm-stat-table__body"
        v-else
      >
        <tr
          v-for="(row, index) in chunkData"
          :key="index"
        >
          <td
            v-for="(td, tdIndex) in cols"
            v-html="td.format ? td.format(row[tdIndex]) : row[tdIndex]"
            :key="tdIndex"
            :class="td.className"
          ></td>
        </tr>
      </tbody>
    </table>

    <div v-if="isLongTable && tableData.length" class="cm-stat-table__footer">
      <div class="cm-stat-table__pagination">
        <button
          :disabled="currentPage < 1"
          class="pagination__btn"
          @click="currentPage -= 1"
        >&lt;</button>
        <button
          v-for="(btn, btnIndex) in btns"
          :key="btnIndex"
          :disabled="activeIndex === btnIndex"
          :class="[
            'pagination__btn',
            { 'pagination__btn--active': activeIndex === btnIndex }]"
          @click="setCurrentPage(btnIndex)">
          {{ btn + 1 }}
        </button>
        <button
          :disabled="currentPage + 2 > chunksQty"
          class="pagination__btn"
          @click="currentPage += 1"
        >&gt;</button>
      </div>
      <span class="cm-stat-table__page-num-info">
        {{ currentPage + 1 }} из {{ chunksQty }}
      </span>
    </div>

    <button
      v-if="!isLongTable && tableData.length > shortChunkSize"
      class="cm-stat-table__open-long-btn"
      @click="$emit('open-long-table')"
    >{{ longLinkTitle }}</button>
  </div>
</template>

<script>
import SpinnerDots from "@/manzana_library/components/Elements/SpinnerDots.vue";
import Input from "@/manzana_library/components/Elements/Input.vue";

export default {
  name: "CmStatTable",

  components: {
    SpinnerDots,
    Input
  },

  props: {
    title: {
      type: String
    },
    longLinkTitle: {
      type: String,
      default: "Открыть всю таблицу"
    },
    value: {
      type: Array,
      default: () => []
    },
    cols: {
      type: Object
    },
    chunkSize: {
      type: Number,
      default: 10
    },
    shortChunkSize: {
      type: Number,
      default: 3
    },
    isLongTable: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isError: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 670
    },
    filterable: {
      type: Array
    },
    sortable: {
      type: Array
    },
    placeholder: {
      type: String,
      default: function () {
        return this.$t('general.enterName');
      },
    }
  },

  data() {
    return {
      currentPage: 0,
      filterInput: "",
      sortField: ""
    }
  },

  computed: {
    keys() {
      return Object.keys(this.cols);
    },
    tableData() {
      let rawData = this.value;

      // TO DO - make searchable with other fields too,
      // now is searchable only with first item from filterable array
      if (this.filterInput) {
        rawData = rawData.filter(
          i => i[this.filterable[0]].includes(this.filterInput)
        );
      }

      return rawData;
    },

    chunkData() {
      if (this.isLongTable) {
        return this.tableData.slice(
          this.currentPage * this.chunkSize,
          this.currentPage * this.chunkSize + this.chunkSize
        );
      }
      return this.tableData.slice(0, this.shortChunkSize);
    },

    activeIndex() {
      if (this.chunksQty === 2 && this.currentPage === 1) {
        return 1;
      }

      if (this.currentPage === 0) {
        return 0;
      }

      if (this.chunksQty - 1 === this.currentPage) {
        return 2;
      }
      return 1;
    },

    btns() {
      if (this.chunksQty === 1) {
        return [0];
      }

      if (this.chunksQty === 2) {
        return [0, 1];
      }

      if (this.activeIndex === 0) {
        return [this.currentPage, this.currentPage + 1, this.currentPage + 2];
      }

      if (this.activeIndex === 2) {
        return [this.currentPage - 2, this.currentPage - 1, this.currentPage];
      }

      return [this.currentPage - 1, this.currentPage, this.currentPage + 1];
    },
    chunksQty() {
      return !this.tableData.length ? 1 : Math.ceil(this.tableData.length / this.chunkSize);
    }
  },

  watch: {
    filterInput() {
      this.currentPage = 0;
    }
  },

  methods: {
    setCurrentPage(index) {
      if (this.chunksQty <= 3) {
        this.currentPage = index;
        return;
      }

      if (index === 0) {
        this.currentPage = this.currentPage - 1;
        return;
      }

      if (index === 2 && this.activeIndex === 0) {
         this.currentPage = this.currentPage + 2;
         return;
      }

      if (index === 1 && this.activeIndex === 2) {
         this.currentPage = this.currentPage - 2;
         return;
      }

      this.currentPage = this.currentPage + 1;
    }
  }
}
</script>

<style lang="scss">
$stat-table-container-height: 285px;

.cm-stat-table {
  width: 100%;

  &__title {
    font-weight: normal;
    font-size: 22px;
    margin-bottom:  15px;
  }

  &__search-input {
    margin-bottom: 20px !important;
    max-width: 360px;
  }

  &-container {
    height: auto;
    // overflow: auto;
  }

  td {
    padding: 15px 20px 15px 0;
    width: fit-content;
  }

  thead {
    border-bottom: 1px solid rgba(#000000, 0.25);
    td {
      font-weight: 600;
      padding-top: 0;
      padding-bottom: 19px;
      white-space: normal;
    }
  }

  tbody {
    td {
      border-bottom: 1px solid rgba(#000000, 0.1);
      white-space: nowrap;
    }
  }

  .bold {
    font-weight: 600;
  }

  .longPadding {
    padding-right: 38px;
  }

  &__body {
    tr {
      vertical-align: baseline;
    }

     &--loading,
     &--error,
     &--empty {
       td {
         height: $stat-table-container-height - 56;
         width: calc(100% + 2px);
       }
     }

    &--loading {
      .spinner-dots {
        margin: 0 auto;
      }
    }

    &--error {
      td {
        font-size: 20px;
        color: $color_error;
        text-align: center;
      }
    }

    &--empty {
      td {
        font-size: 22px;
        color: $color_black_9;
        text-align: center;
      }
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    margin-top: 27px;
  }

  &__page-num-info {
    margin-left: 25px;
    white-space: nowrap;
  }

  &__pagination {
    border: 1px solid #e8e8e8;
    display: flex;
    width: fit-content;

    .pagination {
      &__btn {
        background: $color_white;
        box-shadow: none;
        border-radius: 0;
        box-sizing: border-box;
        color: rgba(#000000, 0.4);
        font-weight: 600;
        font-size: 16px;
        line-height: 22px;
        padding: 0;
        margin: 0;
        text-align: center;
        height: 31px;
        width: 48px;
        transition: opacity 0.3s ease;

        &:not(:last-of-type) {
          border-right: 1px solid #e8e8e8;
        }

        &--active {
          background: $color_green_new;
          color: $color_white;
          opacity: 1;
        }

        &:hover {
          opacity: 0.8;
        }
      }
    }
  }

  &__open-long-btn {
    margin: 0 auto;
    padding: 15px 0 0;
    @include linkBtn();
  }
}
</style>
